/* @flow */

import type { NETGEM_API_V8_METADATA_PROGRAM, NETGEM_API_V8_METADATA_PROVIDER_INFO, NETGEM_API_V8_METADATA_SERIES } from '../../libs/netgemLibrary/v8/types/MetadataProgram';
import type {
  NETGEM_API_VIEWINGHISTORY,
  NETGEM_API_VIEWINGHISTORY_ITEM,
  NETGEM_API_VIEWINGHISTORY_LOCATION,
  NETGEM_API_VIEWINGHISTORY_PLAYED_ITEMS,
  VIEWING_HISTORY_TYPE,
} from '../../libs/netgemLibrary/v8/types/ViewingHistory';
import type { Undefined } from '../jsHelpers/types';
import type { VOD_STATUS_TYPE } from '../ui/metadata/Types';

import { type NETGEM_API_V8_FEED_ITEM } from '../../libs/netgemLibrary/v8/types/FeedItem';
import AccurateTimestamp from '../dateTime/AccurateTimestamp';
import { getIntegerPercentage } from '../maths/maths';
import { VIEWINGHISTORY_START_THRESHOLD, VIEWINGHISTORY_VIEWED_THRESHOLD } from './constantsAndTypes';

const ONE_HUNDRED = 100;

const deepCopyViewingHistoryItem: (item: NETGEM_API_VIEWINGHISTORY_ITEM) => NETGEM_API_VIEWINGHISTORY_ITEM = (item) => {
  const { date, episodes: episodesOriginal, id, playeditems: playeditemsOriginal, rating, watchingstatus } = item;

  // Copy simple values
  const clone: NETGEM_API_VIEWINGHISTORY_ITEM = {
    date,
    episodes: undefined,
    id,
    playeditems: undefined,
    rating,
    watchingstatus,
  };

  // Copy episode array
  if (episodesOriginal) {
    clone.episodes = episodesOriginal.map((episode) => deepCopyViewingHistoryItem(episode));
  }

  // Copy playeditems object
  const playeditems: NETGEM_API_VIEWINGHISTORY_PLAYED_ITEMS = {};
  if (playeditemsOriginal) {
    Object.keys(playeditemsOriginal).forEach((locationId) => {
      const playedItem = playeditemsOriginal[locationId];
      playeditems[locationId] = { ...playedItem };
    });
    clone.playeditems = playeditems;
  }

  return clone;
};

// Returns an integer in the interval [0,100], with a 98-threshold (see VIEWINGHISTORY_VIEWED_THRESHOLD)
const computeProgress: (duration: number, position: number) => number = (duration, position) => {
  const progress = getIntegerPercentage(position, 0, duration);

  return progress < VIEWINGHISTORY_VIEWED_THRESHOLD ? progress : ONE_HUNDRED;
};

const getMostRecentlyPlayedEpisode: (episodes: VIEWING_HISTORY_TYPE) => NETGEM_API_VIEWINGHISTORY_ITEM = (episodes) =>
  episodes.reduce((mostRecentEp, ep) => (ep.playeditems && (!mostRecentEp || ep.date > mostRecentEp.date) ? ep : mostRecentEp));

const getMostRecentlyPlayedItem: (items: NETGEM_API_VIEWINGHISTORY_PLAYED_ITEMS) => NETGEM_API_VIEWINGHISTORY_LOCATION = (items) =>
  Object.values(items).reduce((mostRecentItem, item) => (!mostRecentItem || item.date > mostRecentItem.date ? item : mostRecentItem));

const getHighestProgress: (item: NETGEM_API_VIEWINGHISTORY_ITEM, defaultValue: number, vodViewingHistoryId?: string) => number = (item, defaultValue, vodViewingHistoryId) => {
  const { playeditems } = item;

  if (!playeditems) {
    return defaultValue;
  }

  if (vodViewingHistoryId) {
    // VOD item
    const { [vodViewingHistoryId]: historyItem } = playeditems;
    return historyItem?.percent ?? defaultValue;
  }

  // TV item
  let highestProgress: number | null = null;
  Object.keys(playeditems).forEach((key) => {
    const { percent } = playeditems[key];
    if (typeof percent === 'number' && (highestProgress === null || percent > highestProgress)) {
      highestProgress = percent;
    }
  });

  return typeof highestProgress === 'number' ? highestProgress : defaultValue;
};

// Returns a number between 0 and 100, or null if not found
const getProgress: (
  viewingHistory: NETGEM_API_VIEWINGHISTORY,
  item: NETGEM_API_V8_FEED_ITEM,
  programMetadata: NETGEM_API_V8_METADATA_PROGRAM | null,
  seriesMetadata: NETGEM_API_V8_METADATA_SERIES | null,
  isSeries: boolean,
  vodStatus: VOD_STATUS_TYPE | null,
) => number | null = (viewingHistory, item, programMetadata, seriesMetadata, isSeries, vodStatus) => {
  if (programMetadata) {
    const { providerInfo } = programMetadata;
    const vodViewingHistoryId = vodStatus?.viewingHistoryId;

    if (providerInfo) {
      // Search VOD items
      return getVodProgress(viewingHistory, item, providerInfo, seriesMetadata?.providerInfo, isSeries, vodViewingHistoryId);
    }
  }

  // Search TV items
  return getTVProgress(viewingHistory, item, isSeries, seriesMetadata?.id);
};

const getVodProgress: (
  viewingHistory: NETGEM_API_VIEWINGHISTORY,
  item: NETGEM_API_V8_FEED_ITEM,
  programProviderInfo: NETGEM_API_V8_METADATA_PROVIDER_INFO,
  seriesProviderInfo?: NETGEM_API_V8_METADATA_PROVIDER_INFO,
  isSeries: boolean,
  vodViewingHistoryId?: string,
) => number | null = (viewingHistory, item, programProviderInfo, seriesProviderInfo, isSeries, vodViewingHistoryId) => {
  // New version: using universal Ids for VOD items
  const { viewingHistoryId: programId } = programProviderInfo;

  const { [programId]: programHistory } = viewingHistory;
  if (programHistory) {
    // Single program
    return getHighestProgress(programHistory, programHistory.watchingstatus, vodViewingHistoryId);
  }

  if (seriesProviderInfo) {
    // Program is part of a series
    const { viewingHistoryId: seriesId } = seriesProviderInfo;
    if (seriesId) {
      const { [seriesId]: seriesHistory } = viewingHistory;

      if (seriesHistory) {
        const { episodes } = seriesHistory;
        if (episodes && episodes.length > 0) {
          if (isSeries) {
            // Series tile: display the progress of the most recently played episode
            const mostRecentEpisode = getMostRecentlyPlayedEpisode(episodes);
            return getHighestProgress(mostRecentEpisode, mostRecentEpisode.watchingstatus);
          }

          // Episode tile: display the progress of the episode
          const watchedEpisode = episodes.find((ep) => ep.id === programId);

          if (watchedEpisode) {
            return getHighestProgress(watchedEpisode, watchedEpisode.watchingstatus);
          }
        }
      }
    }
  }

  // Backward compatibility version: using device-specific Ids for VOD items
  return getTVProgress(viewingHistory, item, isSeries);
};

const getTVProgress: (viewingHistory: NETGEM_API_VIEWINGHISTORY, item: NETGEM_API_V8_FEED_ITEM, isSeries: boolean, seriesId?: string) => number | null = (viewingHistory, item, isSeries, seriesId) => {
  const { selectedProgramId } = item;
  const { [selectedProgramId]: programHistory } = viewingHistory;

  if (programHistory) {
    // Single program
    return getHighestProgress(programHistory, programHistory.watchingstatus);
  }

  if (seriesId) {
    // Program is part of a series
    const { [seriesId]: seriesHistory } = viewingHistory;

    if (seriesHistory) {
      const { episodes } = seriesHistory;
      if (episodes && episodes.length > 0) {
        if (isSeries) {
          // Series tile: display the progress of the most recently played episode
          const mostRecentEpisode = getMostRecentlyPlayedEpisode(episodes);
          return getHighestProgress(mostRecentEpisode, mostRecentEpisode.watchingstatus);
        }

        // Episode tile: display the progress of the episode
        const watchedEpisode = episodes.find((ep) => ep.id === selectedProgramId);

        if (watchedEpisode) {
          return getHighestProgress(watchedEpisode, watchedEpisode.watchingstatus);
        }
      }
    }
  }

  return null;
};

const buildViewingHistoryItem: (
  viewingHistory: NETGEM_API_VIEWINGHISTORY,
  locationId: string,
  programId: string,
  seriesId: ?string,
  position: number,
  duration: number,
  start: number,
  end: number,
  channelId?: string,
) => NETGEM_API_VIEWINGHISTORY_ITEM | null = (viewingHistory, locationId, programId, seriesId, position, duration, start, end, channelId) => {
  const date = AccurateTimestamp.nowAsIsoString();

  let episodeItem: ?NETGEM_API_VIEWINGHISTORY_ITEM = null;
  let seriesItem: ?NETGEM_API_VIEWINGHISTORY_ITEM = null;

  if (seriesId) {
    // Series

    // Search series in the viewing history and clone it
    const { [seriesId]: series } = viewingHistory;
    if (series) {
      seriesItem = deepCopyViewingHistoryItem(series);
    }

    if (!seriesItem) {
      seriesItem = {
        date,
        episodes: [],
        id: seriesId,
        rating: 0,
        watchingstatus: 0,
      } as NETGEM_API_VIEWINGHISTORY_ITEM;
    } else {
      const { episodes } = seriesItem;
      if (episodes) {
        episodeItem = episodes.find((ep) => ep.id === programId);
      }
    }
  } else {
    // Single

    // Search episode in the viewing history and clone it
    const { [programId]: episode } = viewingHistory;
    if (episode) {
      episodeItem = deepCopyViewingHistoryItem(episode);
    }
  }

  const shouldEpisodeBeAdded = !episodeItem;

  let playeditems: Undefined<NETGEM_API_VIEWINGHISTORY_PLAYED_ITEMS> = {} as NETGEM_API_VIEWINGHISTORY_PLAYED_ITEMS;

  if (!episodeItem) {
    episodeItem = {
      date: '',
      id: programId,
      playeditems,
      rating: 0,
      watchingstatus: 0,
    } as NETGEM_API_VIEWINGHISTORY_ITEM;
  }

  ({ playeditems } = episodeItem);

  if (!playeditems) {
    playeditems = {} as NETGEM_API_VIEWINGHISTORY_PLAYED_ITEMS;
    episodeItem.playeditems = playeditems;
  }

  let relativePosition = position - start;
  if (relativePosition < VIEWINGHISTORY_START_THRESHOLD) {
    relativePosition = 0;
  }

  const programDuration = duration ? duration - start - end : 0;
  const percent = computeProgress(programDuration, relativePosition);

  playeditems[locationId] = {
    channelId,
    date,
    percent,
    // Make sure position is program duration, in case progress was in [98-100] and was adjuster to 100
    position: percent < ONE_HUNDRED ? relativePosition : programDuration,
  };

  const { watchingstatus } = episodeItem;

  if (watchingstatus < ONE_HUNDRED) {
    const maxPosition = Object.values(playeditems).reduce((acc: number, val: NETGEM_API_VIEWINGHISTORY_LOCATION) => Math.max(acc, val.position), 0);
    const newWatchingstatus = computeProgress(programDuration, maxPosition);

    if (newWatchingstatus > watchingstatus) {
      episodeItem.watchingstatus = newWatchingstatus;
    }
  }

  // Update episode
  episodeItem.date = date;

  if (!seriesId || !seriesItem) {
    // Single
    return episodeItem;
  }

  // Series

  if (!seriesItem.episodes) {
    seriesItem.episodes = [];
  }

  if (shouldEpisodeBeAdded) {
    seriesItem.episodes.push(episodeItem);
  }

  // Update at series level
  seriesItem.date = date;

  return seriesItem;
};

export { buildViewingHistoryItem, getMostRecentlyPlayedEpisode, getMostRecentlyPlayedItem, getProgress };
